import isEmpty from 'lodash.isempty'
import {
  batchCreateBasicField,
  batchCreateNameSuper,
  batchCreateSurname,
  batchEditRectangle,
  convertToSLSRectangle,
  createFieldValue,
  createHighlightsDispatches,
  createStandardizedFieldValue,
  getAllProcessedElements,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  getNestedSubelementsByType,
  getNextUuid,
  getSupersByType,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../shared/services/config'

// Helper function to get the correct super element
function getSuperEl(person, sharedEl) {
  if (sharedEl.superElements.find((se) => se?.id === person?.id)) {
    return person
  }
  let superEl = null
  if (sharedEl.fieldType === 'NAME_SURN') {
    superEl = getNestedSubelementsByType(person, 'NAME')?.find((ne) =>
      getNestedSubelementsByType(ne, 'NAME_SURN').find((ns) => ns.id === sharedEl.id)
    )
  } else {
    sharedEl.superElements.forEach((se) => {
      const nestedSubs = getNestedSubelementsById([person], se.id)
      if (nestedSubs?.length > 0) {
        superEl = nestedSubs[0]
      }
    })
  }
  return superEl
}

// Helper function to add sub-elements
const addSub = (superEl, subElId, partitionKey, subPartitionKey) => ({
  type: 'ADD_SUB',
  value: {
    superPartitionKey: partitionKey,
    subPartitionKey,
    superId: superEl?.id,
    subId: subElId,
    elementType: superEl.elementType,
  },
})

export default function handleSubmitSharedFieldForm(
  values,
  selectedPerson,
  sharedElement,
  newValue,
  state,
  r2State,
  allAvailablePersonsOptions,
  handleSave,
  originalLinkedPersons = [],
  fieldHiglight = null
) {
  const dispatches = []
  let firstPersonDispatches = []
  let newFieldId
  const selectedPersonSuperEl = getSuperEl(selectedPerson, sharedElement)

  const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: R2AppName }
  let fieldValue = createFieldValue(typeof newValue === 'object' ? newValue.originalText : newValue, attribution)

  if (newValue.standard) {
    fieldValue = createStandardizedFieldValue(newValue, sharedElement.fieldType, attribution)
  }

  const personCheckboxes = Object.entries(values)
    .filter(([key]) => key.startsWith('1:1:'))
    .map(([key, value]) => ({ id: key, isChecked: Array.isArray(value) ? value.length > 0 : Boolean(value) }))
    .sort((a) => (a.id === selectedPerson?.id ? -1 : 1)) // put selected person first

  const checkedPersonIds = personCheckboxes.filter((p) => p.isChecked).map((p) => p.id)

  // Sort the original linked persons and the selected persons to compare them and find the added and removed persons
  const initialSelection = originalLinkedPersons.sort()
  const selectedPersonIds = checkedPersonIds.sort()
  const initialSet = new Set(initialSelection)
  const finalSet = new Set(selectedPersonIds)

  // Find the added and removed persons
  const addedPersonIds = [...finalSet].filter((id) => !initialSet.has(id))
  const removedPersonIds = [...initialSet].filter((id) => !finalSet.has(id))

  // Helper function to create the dispatches for the added or updated field value.
  // The returned dispatches will include the highlights dispatches
  const getAddFieldValueDispatches = (elementId) => {
    const newDispatches = [
      {
        type: 'ADD_FV',
        value: {
          fieldValue,
          id: elementId || sharedElement.id,
          partitionKey: sharedElement?.partitionKey,
        },
      },
    ]

    return newDispatches
      .concat(createHighlightsDispatches(state.images, state.currentImageArk, r2State.editHighlights))
      .concat(createHighlightsDispatches(state.images, state.currentImageArk, r2State.referenceEditHighlights))
  }

  // Helper function to create the dispatches for the removed persons.
  const getRemovePersonsDispatches = (removedPersons) => {
    if (!removedPersons || removedPersons.length === 0) return []

    const newDispatches = []
    const flattenedProcessedElements = getAllProcessedElements(state.images)
    let oldElement = sharedElement

    removedPersons.forEach((person) => {
      const superEl = getSuperEl(person, sharedElement)

      if (sharedElement.fieldType === 'NAME_SURN') {
        const oldSurnameId = superEl?.subElements?.find((element) => element.elementType === 'NAME_SURNAME')?.id
        const supers = getSupersByType(sharedElement, 'NAME_SURNAME', flattenedProcessedElements)

        oldElement = oldSurnameId ? supers?.find((element) => element.id === oldSurnameId) || supers[0] : supers[0]
      }

      newDispatches.push({
        type: 'REM_SUB',
        value: {
          superPartitionKey: superEl?.partitionKey,
          subPartitionKey: oldElement?.partitionKey,
          superId: superEl?.id,
          subId: oldElement?.id,
          elementType: superEl.elementType,
        },
      })
    })

    return newDispatches
  }

  // There are four possible scenarios: no changes, added persons, removed persons, or both
  if (addedPersonIds.length === 0 && removedPersonIds.length === 0) {
    // There were no changes on the selection so we only need to update the field value
    dispatches.push(...getAddFieldValueDispatches())
  } else if (addedPersonIds.length === 0 && removedPersonIds.length > 0) {
    // Elements have been removed from the selection so we only need to
    // remove the field value from the removed persons and update the field value
    const removedPersons = removedPersonIds.map((personId) => allAvailablePersonsOptions.find((p) => p.id === personId))
    dispatches.push(...getRemovePersonsDispatches(removedPersons), ...getAddFieldValueDispatches())
  } else {
    // Elements have been added to the selection and removed from the selection so we need to
    // remove the field value from the removed persons, and add the field value to the added persons
    const removedPersons = removedPersonIds.map((personId) => allAvailablePersonsOptions.find((p) => p.id === personId))
    dispatches.push(...getRemovePersonsDispatches(removedPersons))

    // First we need to handle different scenarios based on the fieldType of the shared element
    if (sharedElement.fieldType === 'NAME_SURN') {
      firstPersonDispatches = batchCreateSurname({
        partitionKey: selectedPerson?.partitionKey,
        nameSuperId: selectedPersonSuperEl?.id,
        surnameFieldValue: fieldValue,
      })
      newFieldId = firstPersonDispatches.find(
        (d) => d.type === 'CREATE_ELEMENT' && d?.value?.elementType === 'NAME_SURNAME'
      )?.value?.id
    } else {
      newFieldId = getNextUuid()
      firstPersonDispatches = batchCreateBasicField({
        partitionKey: selectedPerson?.partitionKey,
        superId: selectedPersonSuperEl.id,
        fieldType: sharedElement.fieldType,
        fieldValue,
        superElementType: selectedPersonSuperEl.elementType,
        id: newFieldId,
      })
    }

    dispatches.push(...firstPersonDispatches)

    // Getting the selected persons objects from the checkedPersonIds
    const selectedPersons = checkedPersonIds.map((personId) =>
      allAvailablePersonsOptions.find((p) => p.id === personId)
    )

    const newFieldValueId = firstPersonDispatches.find((d) => d.type === 'ADD_FV')?.value?.id

    // If there was a highlight on the original shared element we need to create a new highlight based for the new field value
    if (fieldHiglight && newFieldValueId) {
      const highlightDispatches = batchEditRectangle({
        fieldId: newFieldValueId,
        oldFieldId: sharedElement.id,
        oldRect: fieldHiglight.originalHighlight,
        partitionKey: selectedPerson.partitionKey,
        oldPartitionKey: sharedElement.partitionKey,
        rect: convertToSLSRectangle(fieldHiglight),
      })

      dispatches.push(...highlightDispatches)
    }

    selectedPersons.forEach((person) => {
      // Check if each person have a common super element with the shared element
      let superEl = getSuperEl(person, sharedElement)

      // If the person doesn't have a super element that matches the shared element, we need to add it
      if (!superEl) {
        // Decide what type of sub element to add based on the shared element fieldType or elementType
        const desiredSubElementType =
          sharedElement.fieldType === 'NAME_SURN' || sharedElement.elementType === 'NAME_SURNAME'
            ? 'NAME_SURNAME'
            : 'FIELD'

        // Matching element equals existing element that matches the fieldType or elementType of sharedElement.
        // This will need to be removed in order to add a new shared field
        const matchingElement = getNestedSubelementsByType(
          person,
          desiredSubElementType === 'FIELD' ? sharedElement.fieldType : desiredSubElementType,
          desiredSubElementType === 'FIELD'
        )

        // If the person has a super element that matches the shared element type, we need to remove the old shared element and add the new one
        if (matchingElement.length > 0) {
          const matchingElementId = matchingElement[0].id
          superEl = matchingElement[0]?.superElements?.find((se) => getNestedOrFloatingElementById([person], se.id))
          superEl = getNestedOrFloatingElementById([person], superEl.id)

          if (person.id !== selectedPerson.id) {
            dispatches.push(addSub(superEl, newFieldId, superEl?.partitionKey, selectedPerson?.partitionKey))
          }

          dispatches.push({
            type: 'REM_SUB',
            value: {
              superPartitionKey: person?.partitionKey,
              subPartitionKey: person?.partitionKey,
              superId: superEl?.id,
              subId: matchingElementId,
              elementType: superEl.elementType,
            },
          })
        }

        // If the desire sub element type is FIELD, we just need to add a new field value to the super element
        if (desiredSubElementType === 'FIELD') {
          dispatches.push(addSub(person, newFieldId, person?.partitionKey, selectedPerson?.partitionKey))
        } else if (desiredSubElementType === 'NAME_SURNAME') {
          // If the desire sub element type is NAME_SURNAME, we need to check if the person has a NAME element to attach the NAME_SURNAME to
          const existingNameEl = getNestedSubelementsByType(person, 'NAME').filter(
            (subEl) => subEl.fieldType !== 'NAME'
          )?.[0]
          const nameElementId = existingNameEl?.id || getNextUuid()

          // If the person doesn't have a NAME element, we need to add it
          if (!existingNameEl) {
            dispatches.push(
              ...batchCreateNameSuper({
                partitionKey: person?.partitionKey,
                nameElementId,
                nameType: 'BIRTH_NAME',
                personId: person?.id,
              })
            )
          }

          // If the person doesn't have a NAME_SURNAME element, we need to add it
          if (isEmpty(matchingElement) && person.id !== selectedPerson.id) {
            dispatches.push(
              addSub(
                { id: nameElementId, elementType: 'NAME' },
                newFieldId,
                person?.partitionKey,
                selectedPerson?.partitionKey
              )
            )
          }
        }
      } else {
        // If the person has a super element that matches the shared element, we just need to add the new field value
        if (person.id !== selectedPerson.id) {
          dispatches.push(addSub(superEl, newFieldId, superEl?.partitionKey, selectedPerson?.partitionKey))
        }

        // We need to remove old shared element from the person
        dispatches.push(...getRemovePersonsDispatches([person]))
      }
    })
  }

  if (selectedPerson?.recordId) {
    dispatches.push({ type: 'SET_RECORD_PERSON_IDS', value: selectedPerson.recordId })
  }

  // Clear out the localizedNames value to trigger PersonOverview to fetch it again if the surname is updated.
  if (sharedElement?.fieldType === 'NAME_SURN') {
    const involvedPersons = [...selectedPersonIds, ...removedPersonIds]

    involvedPersons.forEach((personId) => {
      // First try to find person in values.persons, then fall back to allAvailablePersonsOptions
      // This ensures we find the partitionKey for all involved persons, including those not in the form
      const person =
        values?.persons?.find((p) => p.id === personId) || allAvailablePersonsOptions?.find((p) => p.id === personId)
      if (!person?.partitionKey) return
      dispatches.push({
        type: 'SET_PERSON_LOCALIZED_NAME',
        value: {
          personId,
          localizedName: '',
          partitionKey: person.partitionKey,
        },
      })
    })
  }

  handleSave(dispatches)
}
